/**
 * Move the on-screen counts to match a save, WITHOUT refetching.
 *
 * Same contract as the table and shelf versions: refetching would pull the page out from
 * under the labeller — a clip leaves the «Неразмеченные» queue the instant it is judged,
 * and a mistake has to stay fixable until they choose to reload. So the list is left
 * exactly as it is and only the counters move.
 *
 * The speech-specific part is what counts as judged: `""` DOES. A blank is a human saying
 * "this is noise", which is a real training target, so a clip marked noise leaves the
 * unlabelled queue exactly like a transcribed one. Only `null` is unjudged.
 */
import type { AudioPoolSamples, View } from '@/api/audioPool';

const UNLABELLED_VIEWS: View[] = ['unlabelled', 'imported', 'collected'];

export function applyAudioDelta(
  cur: AudioPoolSamples,
  view: View,
  /** id → the value just written; `null` means the verdict was retracted. */
  written: Map<number, string | null>,
): AudioPoolSamples {
  const labelled = { ...cur.facets.labelled };
  const origins = { ...cur.facets.unlabelled_origins };
  let total = cur.total;

  for (const it of cur.items) {
    if (!written.has(it.id)) continue;
    const now = written.get(it.id) ?? null;
    // `!= null`, never a falsy test: "" is a verdict, not the absence of one.
    const wasLabelled = it.corrected_text != null;
    const isLabelled = now != null;
    if (wasLabelled === isLabelled && it.source !== 'ai') continue;

    let left = false;
    if (!wasLabelled && isLabelled) {
      labelled.unlabelled -= 1;
      origins[it.origin] = (origins[it.origin] ?? 0) - 1;
      labelled.all += 1;
      labelled.human += 1;
      left = UNLABELLED_VIEWS.includes(view);
    } else if (wasLabelled && !isLabelled) {
      labelled.unlabelled += 1;
      origins[it.origin] = (origins[it.origin] ?? 0) + 1;
      labelled.all -= 1;
      if (it.source === 'ai') labelled.ai -= 1; else labelled.human -= 1;
      left = !UNLABELLED_VIEWS.includes(view);
    } else if (it.source === 'ai' && isLabelled) {
      labelled.ai -= 1;
      labelled.human += 1;
      left = view === 'ai';
    }
    if (left) total -= 1;
  }
  return {
    ...cur,
    total,
    facets: { ...cur.facets, labelled, unlabelled_origins: origins },
  };
}
